import { useState } from 'react';
import { usePanelToast } from '../../../context/PanelToastContext';
import { applyPaymentBalance } from '../../../services/receptionist/receptionistApi';
import {
  defaultPaymentSplits,
  serializePaymentSplits,
  sumPaymentSplits,
} from '../../../utils/splitPaymentUtils';
import { formatCurrency } from '../receptionistData';
import { notifyPaymentsUpdated } from '../paymentStore';
import SplitPaymentFields from './SplitPaymentFields';
import { buildRemainingClearReceipt } from './buildSaleReceipt';
import { saleRowLeft, saleRowPaid, saleRowTotal } from './salePaymentUtils';
import '../payments.css';
import './ancillarySales.css';
function todayDate() {
  return new Date().toISOString().slice(0, 10);
}

export default function PayBalanceModal({
  paymentRow,
  saleMeta,
  onClose,
  onPaid,
}) {
  const toast = usePanelToast();
  const [splits, setSplits] = useState(defaultPaymentSplits());
  const [date, setDate] = useState(todayDate());
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  if (!paymentRow) return null;

  const total = saleRowTotal(paymentRow);
  const paidSoFar = saleRowPaid(paymentRow);
  const left = saleRowLeft(paymentRow);
  const installment = sumPaymentSplits(splits);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (left <= 0) {
      toast.showError('Nothing left to pay on this sale.');
      return;
    }
    if (!Number.isFinite(installment) || installment <= 0) {
      toast.showError('Enter amounts for at least one payment method.');
      return;
    }
    if (installment > left) {
      toast.showError(`Payment cannot exceed ${formatCurrency(left)} remaining.`);
      return;
    }

    setSaving(true);
    try {
      const serialized = serializePaymentSplits(splits);
      const updated = await applyPaymentBalance({
        paymentDbId: paymentRow.rawId,
        additionalSplits: serialized,
        date,
        paymentNote: note,
      });

      const receipt = buildRemainingClearReceipt(updated, {
        installmentAmount: installment,
        installmentSplits: serialized,
        transactionDate: date,
        balanceBefore: left,
        pendingAfterPay: Math.max(0, left - installment),
        saleMeta: saleMeta || { itemFieldLabel: 'Package' },
      });

      notifyPaymentsUpdated();
      toast.showSaved('Balance payment recorded');
      onPaid?.(receipt, updated);
      onClose();
    } catch (err) {
      toast.showError(err.message || 'Failed to record payment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="subscription-modal-overlay rec-pay-balance-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >      <div
        className="subscription-modal"
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="subscription-modal-header">
          <h2 className="subscription-modal-title">Pay remaining balance</h2>
          <button
            type="button"
            className="subscription-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="rec-payment-receipt-preview rec-pay-balance-summary">
          <div className="rec-payment-receipt-row">
            <span>Invoice</span>
            <strong>{paymentRow.invoiceId || '—'}</strong>
          </div>
          <div className="rec-payment-receipt-row">
            <span>Member</span>
            <strong>{paymentRow.member}</strong>
          </div>
          <div className="rec-payment-receipt-row">
            <span>Total amount</span>
            <strong>{formatCurrency(total)}</strong>
          </div>
          <div className="rec-payment-receipt-row">
            <span>Already paid</span>
            <strong>{formatCurrency(paidSoFar)}</strong>
          </div>
          <div className="rec-payment-receipt-row rec-payment-receipt-row-highlight">
            <span>Balance due</span>
            <strong>{formatCurrency(left)}</strong>
          </div>
        </div>

        <form className="subscription-form" onSubmit={handleSubmit}>
          <SplitPaymentFields
            idPrefix="pay-balance"
            splits={splits}
            onChange={setSplits}
            disabled={saving}
          />
          <div className="rec-sale-payment-summary">
            <p className="owner-placeholder-text" style={{ margin: 0, fontSize: 13 }}>
              This payment: <strong>{formatCurrency(installment)}</strong>
            </p>
            <p className="rec-sale-pending-amount">
              Pending after pay: <strong>{formatCurrency(Math.max(0, left - installment))}</strong>
            </p>
          </div>

          <div className="owner-form-group">
            <label className="owner-form-label" htmlFor="pay-balance-date">Payment date</label>
            <input
              id="pay-balance-date"
              type="date"
              className="owner-form-input"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
              disabled={saving}
            />
          </div>

          <div className="owner-form-group">
            <label className="owner-form-label" htmlFor="pay-balance-note">Note (optional)</label>
            <input
              id="pay-balance-note"
              className="owner-form-input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Balance payment note"
              disabled={saving}
            />
          </div>

          <div className="subscription-form-actions">
            <button
              type="submit"
              className="owner-btn owner-btn-primary"
              disabled={saving}
            >
              {saving ? 'Saving…' : `Pay ${formatCurrency(installment || left)}`}
            </button>
            <button
              type="button"
              className="owner-btn owner-btn-secondary"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
